/**
 * @fileoverview `useMarketingShellTranslate(t)` — the translator for the words marketing-shell
 * itself renders (LIFE-SHORTLISTED-001 F-Q7-1).
 * @package @puffin-core/marketing-shell
 *
 * Resolution order: the consumer's `t` (honoured first) → the `marketingShell` namespace on the
 * instance in the React tree → `MARKETING_SHELL_EN`, so an unwired tree (the blog's server render)
 * renders readable English, never a raw key.
 *
 * `braces: 'single'` + `onMissing: 'keep'`: a placeholder whose param did not arrive is echoed back
 * (`{owner}`), so a missing param stays VISIBLE rather than blanking the sentence.
 *
 * ⚠️ Hook-placement rule (RECIPE §3): call this immediately above the first hook or expression that
 * consumes the translator.
 *
 * @module @puffin-core/marketing-shell/hooks/useMarketingShellTranslate
 */

import { createInterpolator } from '@puffin-core/i18n-bootstrap/strings-kit';
import {
  createNamespaceTranslate,
  createNamespaceTranslator,
} from '@puffin-core/i18n-bootstrap/namespace-translate';

import { MARKETING_SHELL_NAMESPACE } from '../strings/namespace.js';
import { resolveString } from '../strings/resolveString.js';

/** marketing-shell's ONE translator configuration: the hook and the pure chain are built from it alike. */
const MARKETING_SHELL_TRANSLATE = Object.freeze({
  namespace: MARKETING_SHELL_NAMESPACE,
  resolveString,
  interpolate: createInterpolator({ braces: 'single', onMissing: 'keep' }),
});

export const useMarketingShellTranslate = createNamespaceTranslate(MARKETING_SHELL_TRANSLATE);

const translatorOf = createNamespaceTranslator(MARKETING_SHELL_TRANSLATE);

/**
 * The hook's chain over an instance, for a render outside React (STORE-SITES-CONVERGENCE-001 wave 1, R1 store: a store
 * page's synthesized words, which its head needs before its tree renders). It answers exactly what
 * `useMarketingShellTranslate()` answers under that instance.
 *
 * @param {import('i18next').i18n} i18n - an instance carrying the `marketingShell` namespace (`createSeededI18n`)
 * @returns {(key: string, params?: Object) => string|undefined}
 * @throws {TypeError} For a value that is not an i18next instance.
 */
export function marketingShellTranslatorOf(i18n) {
  if (!i18n || typeof i18n.getFixedT !== 'function') {
    throw new TypeError('marketingShellTranslatorOf: an i18next instance is required');
  }
  return translatorOf(i18n.getFixedT(null, MARKETING_SHELL_NAMESPACE));
}
